import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi } from '../api/auth'
import type { UserRole } from '../types'
import { scopeGrants } from '../config/apps'

/** 서버가 ClaimTypes.Role 로 넣은 역할은 짧은 이름 "role" 이 아니라 이 URI 키로 직렬화된다 */
const ROLE_CLAIM_URI = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'

interface JwtPayload {
  sub?: string
  /** 접근 가능한 앱 목록 — 하나면 문자열, 여러 개면 배열로 직렬화된다 */
  aud?: string | string[]
  role?: string
  [ROLE_CLAIM_URI]?: string
  companyId?: string
  /** 앱 단위 추가 권한 — 하나면 문자열, 여러 개면 배열 */
  scope?: string | string[]
}

const parseJwt = (token: string): JwtPayload => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(payload)) ?? {}
  } catch { return {} }
}

const asList = (v: string | string[] | undefined): string[] =>
  Array.isArray(v) ? v : v ? [v] : []

const parseApps = (payload: JwtPayload): string[] => asList(payload.aud)
const parseRole = (payload: JwtPayload): string | undefined => payload.role ?? payload[ROLE_CLAIM_URI]

/**
 * 토큰에서 파생되는 세션 필드 일체. 저장소에는 토큰만 남기고 role·scopes·apps 등은 항상 여기서 다시 계산한다 —
 * 파생값을 저장하면 배포로 규칙이 바뀔 때(scope·역할 개명 등) 낡은 값이 localStorage 에 굳는다.
 */
const deriveFromToken = (accessToken: string | null) => {
  if (!accessToken) return { role: null, scopes: [] as string[], companyId: null, userId: null, apps: [] as string[] }
  const payload = parseJwt(accessToken)
  return {
    role: (parseRole(payload) as UserRole | undefined) ?? null,
    scopes: asList(payload.scope),
    companyId: payload.companyId || null,
    userId: payload.sub ?? null,
    apps: parseApps(payload),
  }
}

interface AuthStore {
  accessToken: string | null
  refreshToken: string | null
  /** 토큰의 역할 그대로 — 옛 우산 scope 의 Admin 승격은 폐지, 화면 분기는 role 과 can()을 함께 쓴다. */
  role: UserRole | null
  /** 앱 단위 추가 권한(scope 클레임). 화면 분기는 보통 can()을 쓴다. */
  scopes: string[]
  companyId: string | null
  userId: string | null
  /** 토큰 aud — 이 계정(회사)이 켜둔 앱 id 목록. 리프레시마다 갱신된다. */
  apps: string[]
  setAuth: (accessToken: string, refreshToken: string, role: string, companyId?: string) => void
  setTokens: (accessToken: string, refreshToken: string) => void
  logout: () => void
  isAuthenticated: () => boolean
  hasApp: (appId: string) => boolean
  /** scope 클레임만으로 해당 권한을 갖는가 (manage ⊇ view, 폐지된 우산 인정 — config/apps.ts scopeGrants) */
  hasScope: (scope: string) => boolean
  /** 역할 Admin 이거나 해당 scope 보유 — 서버 EdgeAuthorization.CanAccess 와 같은 규칙. 화면 분기는 이걸 쓴다. */
  can: (scope: string) => boolean
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      role: null,
      scopes: [],
      companyId: null,
      userId: null,
      apps: [],

      setAuth: (accessToken, refreshToken, role, companyId) => {
        const d = deriveFromToken(accessToken)
        set({
          accessToken,
          refreshToken,
          ...d,
          // 토큰에 없을 때만 로그인 응답 값으로 보강 (구버전 서버 호환)
          role: d.role ?? (role as UserRole),
          companyId: d.companyId ?? companyId ?? null,
        })
      },

      // 리프레시 — 회사 앱 토글·역할·scope 변경이 재로그인 없이 여기서 반영된다
      setTokens: (accessToken, refreshToken) => {
        const d = deriveFromToken(accessToken)
        set(s => ({
          accessToken,
          refreshToken,
          apps: d.apps,
          scopes: d.scopes,
          role: d.role ?? s.role,
          companyId: d.companyId || s.companyId,
          userId: d.userId ?? s.userId,
        }))
      },

      logout: () => {
        const { refreshToken } = get()
        if (refreshToken) authApi.logout(refreshToken).catch(() => {})
        set({ accessToken: null, refreshToken: null, role: null, scopes: [], companyId: null, userId: null, apps: [] })
      },

      isAuthenticated: () => !!get().accessToken,
      hasApp: (appId) => get().apps.includes(appId),
      hasScope: (scope) => get().scopes.some(held => scopeGrants(held, scope)),
      can: (scope) => get().role === 'Admin' || get().hasScope(scope),
    }),
    {
      name: 'edge-auth',
      // 토큰만 저장하고 role·scopes·apps 등 파생값은 로드 때마다 토큰에서 재계산 —
      // 예전 배포에서 저장된 낡은 파생값(예: scope 세분화 전의 실효 role)이 세션에 굳는 것을 막는다.
      partialize: (s) => ({ accessToken: s.accessToken, refreshToken: s.refreshToken }),
      merge: (persisted, current) => {
        const p = persisted as Partial<Pick<AuthStore, 'accessToken' | 'refreshToken'>> | undefined
        const accessToken = p?.accessToken ?? null
        return {
          ...current,
          accessToken,
          refreshToken: p?.refreshToken ?? null,
          ...deriveFromToken(accessToken),
        }
      },
    }
  )
)

// 탭 간 세션 동기화 — 리프레시 토큰은 1회용(rotation)이라 한 탭이 갱신하면 다른 탭의 메모리 토큰은 낡는다.
// persist 는 저장만 하고 storage 이벤트를 듣지 않으므로, 다른 탭이 갱신·로그인·로그아웃하면 여기서 따라간다.
// (keepalive 타이머는 accessToken 변경 구독으로 새 만료 기준에 맞춰 다시 예약된다 — auth/session.ts)
window.addEventListener('storage', (e) => {
  if (e.key === 'edge-auth') void useAuthStore.persist.rehydrate()
})
